/**
 * Admin Service — platform master operations
 *
 * Every function here is a thin wrapper over a SECURITY DEFINER RPC that checks
 * is_platform_master() before doing anything. This module holds no authorization
 * logic and must not grow any: a non-master calling these gets 42501 from
 * Postgres, and that is the only thing standing between them and every tenant's
 * data. Hiding the menu is cosmetic.
 *
 * The RPC argument names below (p_user, p_workspace, p_role) are contract, not
 * style. PostgREST resolves a function by argument NAME — invitationService.ts
 * passes { token } to accept_invitation(token_param) and every call dies with
 * PGRST202. Changing a key here without changing 20240101000020 breaks the
 * feature silently, and no psql test will catch it.
 *
 * @module services/adminService
 */

import { getSupabaseClient, isSupabaseConfigured } from "../config/supabase";
import { logError } from "./logger";
import type { ServiceResult, WorkspaceRole } from "../types";

export interface AdminUserWorkspace {
  id: string;
  name: string;
  role: WorkspaceRole;
}

export interface AdminUser {
  id: string;
  email: string;
  createdAt: string;
  lastSignInAt: string | null;
  workspaces: AdminUserWorkspace[];
}

export interface AdminWorkspace {
  id: string;
  name: string;
  slug: string;
  memberCount: number;
  hasMediaData: boolean;
}

interface AdminUserRow {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  workspaces: AdminUserWorkspace[];
}

interface AdminWorkspaceRow {
  id: string;
  name: string;
  slug: string;
  member_count: number;
  has_media_data: boolean;
}

/**
 * True when the signed-in user is the platform master.
 *
 * Returns false on any error rather than throwing: this decides whether a menu
 * renders, and a network blip should hide the menu, not break the app. It is
 * NOT a security check — the RPCs enforce that themselves.
 */
export const checkIsPlatformMaster = async (): Promise<boolean> => {
  if (!isSupabaseConfigured()) return false;

  const supabase = getSupabaseClient();

  try {
    const { data, error } = await supabase.rpc("is_platform_master");
    if (error) {
      logError("AdminService", "Error checking platform master", error);
      return false;
    }
    return data === true;
  } catch (err) {
    logError("AdminService", "Error checking platform master", err);
    return false;
  }
};

/**
 * Every user, with the workspaces they can reach.
 */
export const listUsers = async (): Promise<ServiceResult<AdminUser[]>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Supabase not configured" };
  }

  const supabase = getSupabaseClient();

  try {
    const { data, error } = await supabase.rpc("admin_list_users");

    if (error) {
      logError("AdminService", "Error listing users", error);
      return { success: false, error: "Failed to load users" };
    }

    // `workspaces` arrives as jsonb from the RPC, so the generated type can
    // only call it `Json`. Asserting the shape needs the two-step cast; the
    // RPC's own SQL is what guarantees it.
    const rows = (data ?? []) as unknown as AdminUserRow[];

    return {
      success: true,
      data: rows.map((row) => ({
        id: row.id,
        email: row.email,
        createdAt: row.created_at,
        lastSignInAt: row.last_sign_in_at,
        workspaces: row.workspaces ?? [],
      })),
    };
  } catch (err) {
    logError("AdminService", "Error listing users", err);
    return { success: false, error: "Failed to load users" };
  }
};

/**
 * Every workspace — the destinations the master can grant access to.
 */
export const listWorkspaces = async (): Promise<ServiceResult<AdminWorkspace[]>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Supabase not configured" };
  }

  const supabase = getSupabaseClient();

  try {
    const { data, error } = await supabase.rpc("admin_list_workspaces");

    if (error) {
      logError("AdminService", "Error listing workspaces", error);
      return { success: false, error: "Failed to load workspaces" };
    }

    const rows = (data ?? []) as AdminWorkspaceRow[];

    return {
      success: true,
      data: rows.map((row) => ({
        id: row.id,
        name: row.name,
        slug: row.slug,
        memberCount: Number(row.member_count),
        hasMediaData: row.has_media_data === true,
      })),
    };
  } catch (err) {
    logError("AdminService", "Error listing workspaces", err);
    return { success: false, error: "Failed to load workspaces" };
  }
};

/**
 * Grant a user access to a workspace, or change the role they already have.
 *
 * One call covers both — the RPC UPSERTs on UNIQUE (workspace_id, user_id).
 */
export const setWorkspaceRole = async (
  userId: string,
  workspaceId: string,
  role: WorkspaceRole
): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Supabase not configured" };
  }

  const supabase = getSupabaseClient();

  try {
    const { error } = await supabase.rpc("admin_set_workspace_role", {
      p_user: userId,
      p_workspace: workspaceId,
      p_role: role,
    });

    if (error) {
      logError("AdminService", "Error setting workspace role", error);
      return { success: false, error: "Failed to update access" };
    }

    return { success: true, data: undefined };
  } catch (err) {
    logError("AdminService", "Error setting workspace role", err);
    return { success: false, error: "Failed to update access" };
  }
};

/**
 * Create a user account. The privileged work happens in /api/admin/create-user;
 * this only proves who is asking.
 *
 * The access token is read fresh from the session on every call rather than
 * cached: autoRefreshToken rotates it, and a stale token means a 401 the master
 * cannot explain.
 */
export const createUser = async (
  email: string,
  password: string
): Promise<ServiceResult<{ id: string }>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Supabase not configured" };
  }

  const supabase = getSupabaseClient();

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) {
      return { success: false, error: "Not signed in" };
    }

    // The bearer token is the ONLY identity claim. Sending a user id or an
    // is-master flag here would be meaningless — the route ignores the body
    // for identity and asks Postgres instead.
    const response = await fetch("/api/admin/create-user", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email, password }),
    });

    // Guarded like deleteUser's twin below: a proxy's HTML 502 or an empty
    // body made this throw a SyntaxError out of a function whose whole
    // contract is to REPORT failure, so the caller saw a crash instead of
    // "could not create the user".
    const payload = (await response.json().catch(() => ({}))) as { id?: string; error?: string };

    if (!response.ok || !payload.id) {
      return { success: false, error: payload.error ?? "Could not create the user" };
    }

    return { success: true, data: { id: payload.id } };
  } catch (err) {
    logError("AdminService", "Error creating user", err);
    return { success: false, error: "Could not reach the server" };
  }
};

/**
 * Permanently delete a user. The privileged work happens in
 * /api/admin/delete-user; this only proves who is asking.
 *
 * Read the token fresh on every call rather than caching it: autoRefreshToken
 * rotates it, and a stale one means a 401 the master cannot explain.
 */
export const deleteUser = async (userId: string): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Supabase not configured" };
  }

  const supabase = getSupabaseClient();

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) {
      return { success: false, error: "Not signed in" };
    }

    const response = await fetch("/api/admin/delete-user", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });

    const payload = (await response.json().catch(() => ({}))) as { error?: string };

    if (!response.ok) {
      return { success: false, error: payload.error ?? "Could not delete the user" };
    }

    return { success: true };
  } catch (err) {
    logError("AdminService", "Error deleting user", err);
    return { success: false, error: "Could not reach the server" };
  }
};

/**
 * Revoke a user's access to a workspace.
 */
export const removeMember = async (
  userId: string,
  workspaceId: string
): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Supabase not configured" };
  }

  const supabase = getSupabaseClient();

  try {
    const { error } = await supabase.rpc("admin_remove_member", {
      p_user: userId,
      p_workspace: workspaceId,
    });

    if (error) {
      logError("AdminService", "Error removing member", error);
      return { success: false, error: "Failed to revoke access" };
    }

    return { success: true, data: undefined };
  } catch (err) {
    logError("AdminService", "Error removing member", err);
    return { success: false, error: "Failed to revoke access" };
  }
};

/**
 * Create an empty workspace. Returns the new workspace id.
 */
export const createWorkspace = async (
  name: string
): Promise<ServiceResult<string>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Supabase not configured" };
  }

  const supabase = getSupabaseClient();

  try {
    const { data, error } = await supabase.rpc("admin_create_workspace", {
      p_name: name,
    });

    if (error) {
      logError("AdminService", "Error creating workspace", error);
      return { success: false, error: "Failed to create workspace" };
    }

    return { success: true, data: data as string };
  } catch (err) {
    logError("AdminService", "Error creating workspace", err);
    return { success: false, error: "Failed to create workspace" };
  }
};

/**
 * Rename a workspace.
 */
export const renameWorkspace = async (
  id: string,
  name: string
): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Supabase not configured" };
  }

  const supabase = getSupabaseClient();

  try {
    const { error } = await supabase.rpc("admin_rename_workspace", {
      p_workspace: id,
      p_name: name,
    });

    if (error) {
      logError("AdminService", "Error renaming workspace", error);
      return { success: false, error: "Failed to rename workspace" };
    }

    return { success: true, data: undefined };
  } catch (err) {
    logError("AdminService", "Error renaming workspace", err);
    return { success: false, error: "Failed to rename workspace" };
  }
};

/**
 * Delete a workspace. When the workspace has linked media data the RPC raises
 * with a message naming what is linked; that message is surfaced verbatim, since
 * it IS the report the master needs ("...has linked media data (campaigns=3)...").
 */
export const deleteWorkspace = async (
  id: string
): Promise<ServiceResult<void>> => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: "Supabase not configured" };
  }

  const supabase = getSupabaseClient();

  try {
    const { error } = await supabase.rpc("admin_delete_workspace", {
      p_workspace: id,
    });

    if (error) {
      logError("AdminService", "Error deleting workspace", error);
      // The block (P0001) carries a useful message; pass it through rather than
      // flattening it to a generic string. Other errors have serviceable
      // messages too, so this is honest for all of them.
      return { success: false, error: error.message || "Failed to delete workspace" };
    }

    return { success: true, data: undefined };
  } catch (err) {
    logError("AdminService", "Error deleting workspace", err);
    return { success: false, error: "Failed to delete workspace" };
  }
};
